import { API } from 'aws-amplify';
import {
  mapQuotesByUnit,
  transformUnitFromApi,
  transformStageName,
  transformFromGetPrimaryOffer,
  transformFromGetOpportunity,
  transformHouseholdReferences,
  activeOCRFromApi,
  transformPayment,
  transformRoleToApi,
  transformReferencing,
  mapRenewalQuotes,
  mapNames,
} from './transform';

const ACTIVE_OCR_STAGE_PRIORITY = [
  'SEND_OFFER',
  'OFFER_SELECTED',
  'REFERENCES_REQUESTED',
  'ON_REFERENCING',
  'MOVE_IN_MONIES',
  'SIGNING',
  'AUDIT',
  'PRE_VIEWING',
  'BOOKED_VIEWING',
  'POST_VIEWING',
];

const selectPreferredActiveOcrRecord = (records = []) => {
  if (!Array.isArray(records) || !records.length) return null;

  const mappedRecords = records.map((record) => ({
    record,
    mapped: activeOCRFromApi(record),
  }));

  const prioritizedRecord = ACTIVE_OCR_STAGE_PRIORITY.reduce((selectedRecord, stageKey) => {
    if (selectedRecord) return selectedRecord;
    return mappedRecords.find(({ mapped }) => mapped && mapped.opportunityStage === stageKey) || null;
  }, null);

  return prioritizedRecord ? prioritizedRecord.mapped : mappedRecords?.[0]?.mapped || null;
};

const getOpportunityStage = async (opportunityId) => {
  const { result = {} } = await API.get('ApiGW', `/salesforce/opportunity/${opportunityId}/stageName`);
  return result?.records?.[0]?.StageName ? transformStageName(result.records[0].StageName) : undefined;
};

const getOpportunityPayments = async (opportunityId, subType) => {
  const { result = {} } = await API.get('ApiGW', `/salesforce/opportunity/${opportunityId}/payments?subType=${subType}`);
  return result?.records?.[0] ? transformPayment(result.records[0]) : undefined;
};

const getContactAuthorisations = async (contactId, paymentRoute) => {
  const { result = {} } = await API.get('ApiGW', `/salesforce/contact/${contactId}/authorisations?paymentRoute=${paymentRoute}`);
  return result?.records?.[0] ? transformPayment(result.records[0]) : undefined;
};

const getContactAuthorisationsList = async (contactId, paymentRoute) => {
  const { result = {} } = await API.get('ApiGW', `/salesforce/contact/${contactId}/authorisations?paymentRoute=${paymentRoute}`);
  return (result.records || []).map(transformPayment);
};

const getPaymentRecord = async (paymentId) => {
  const { result = {} } = await API.get('ApiGW', `/salesforce/payment/${paymentId}`);
  return result && transformPayment(result);
};

const getAuthorisationRecord = async (authorisationId) => {
  const { result = {} } = await API.get('ApiGW', `/salesforce/authorisation/${authorisationId}`);
  return result && transformPayment(result);
};

const getOcrDdAmount = async (ocrId) => {
  const { result = {} } = await API.get('ApiGW', `/salesforce/opportunityContactRole/${ocrId}/amount`);
  return result?.records?.[0]?.Direct_Debit_Amount__c;
};

const getOcrReferencing = async (opportunityId, contactId, retryApplicantId = false) => {
  const { result = [] } = await API.get('ApiGW', `/salesforce/opportunityContactRole/${opportunityId}/${contactId}${retryApplicantId ? '?retry_applicant_id=true' : ''}`);
  return result?.[0] ? transformReferencing(result[0]) : undefined;
};

const parseEnumValue = (value) => {
  if (value === undefined || value === null || value === '') return undefined;
  if (typeof value === 'number') return value;
  const match = String(value).match(/^\s*(\d+)/);
  const parsed = match ? Number(match[1]) : Number(value);
  return Number.isNaN(parsed) ? undefined : parsed;
};

const parseNumber = (value) => {
  if (value === undefined || value === null || value === '') return undefined;
  const numberValue = Number(value);
  return Number.isNaN(numberValue) ? undefined : numberValue;
};

const normaliseNiNumber = (value) => {
  if (!value) return undefined;
  const cleaned = String(value).replace(/\s+/g, '').toUpperCase();
  return cleaned || undefined;
};

const buildPetInfoSummary = (values = {}) => {
  const parts = [
    values.petType === '1' ? 'Cat' : values.petType === '2' ? 'Dog' : values.petType === '3' ? (values.petTypeDetail || 'Other') : undefined,
    values.petName ? `Name: ${values.petName}` : undefined,
    values.petBreed ? `Breed: ${values.petBreed}` : undefined,
    values.petAge !== undefined && values.petAge !== null && values.petAge !== '' ? `Age: ${values.petAge}` : undefined,
    values.petSex === '1' ? 'Male' : values.petSex === '2' ? 'Female' : undefined,
  ].filter(Boolean);

  return parts.length ? parts.join(', ') : undefined;
};

const buildUkAddress = (values = {}, prefix = '') => {
  const address = {
    houseNumber: values[`${prefix}HouseNumber`],
    houseName: values[`${prefix}HouseName`],
    addressLine1: values[`${prefix}AddressLine1`],
    addressLine2: values[`${prefix}AddressLine2`],
    town: values[`${prefix}Town`],
    postcode: values[`${prefix}Postcode`],
    ukCountryCode: (
      values[`${prefix}HouseNumber`]
      || values[`${prefix}HouseName`]
      || values[`${prefix}AddressLine1`]
      || values[`${prefix}AddressLine2`]
      || values[`${prefix}Town`]
      || values[`${prefix}Postcode`]
    ) ? 1 : undefined,
  };

  return cleanObject(address);
};

const cleanObject = (value) => {
  if (Array.isArray(value)) {
    const cleanedArray = value.map(cleanObject).filter((item) => item !== undefined);
    return cleanedArray.length ? cleanedArray : undefined;
  }

  if (value && typeof value === 'object') {
    const cleaned = Object.entries(value).reduce((acc, [key, childValue]) => {
      const cleanedChild = cleanObject(childValue);
      if (cleanedChild !== undefined) {
        acc[key] = cleanedChild;
      }
      return acc;
    }, {});
    return Object.keys(cleaned).length ? cleaned : undefined;
  }

  if (value === '' || value === null || value === undefined) return undefined;
  return value;
};

const mapHomeletFormToPayload = (values = {}) => {
  const incomeTypeID = parseEnumValue(values.incomeType);
  const parsedTenancyTermMonths = parseNumber(values.tenancyTermMonths);
  const income = {
    incomeTypeID,
    incomeTermID: parseEnumValue(values.incomeTerm),
  };

  if (incomeTypeID === 1) {
    const grossAnnualIncome = parseNumber(values.employmentGrossAnnualIncome);
    const netMonthlyIncome = parseNumber(values.employmentNetMonthlyIncome)
      || Number((grossAnnualIncome / 12).toFixed(2));
    income.employed = {
      employersName: values.employerName,
      jobTitle: values.jobTitle,
      employmentGrossAnnualIncome: grossAnnualIncome,
      netMonthlyIncome,
      employmentBonuses: Boolean(values.employmentBonuses),
      employmentOvertimeReceived: Boolean(values.employmentOvertimeReceived),
      employmentBonusTerm: parseEnumValue(values.employmentBonusTerm),
      employmentAvgBonusIncome: parseNumber(values.employmentAvgBonusIncome),
      employmentOvertimeTerm: parseEnumValue(values.employmentOvertimeTerm),
      employmentOvertimeAvgIncome: parseNumber(values.employmentOvertimeAvgIncome),
      employmentEmployeeNumber: values.employmentEmployeeNumber,
      addressLine1: values.employmentAddressLine1 || values.historyAddressLine1 || values.addressLine1,
      addressLine2: values.employmentAddressLine2,
      postCode: values.employmentPostCode || values.historyPostcode || values.postcode,
      city: values.employmentCity || values.historyTown || values.town,
      industryType: parseEnumValue(values.industryType),
      employmentTypeId: parseEnumValue(values.employmentTypeId),
      paymentScheduleId: parseEnumValue(values.paymentScheduleId),
      startDateofEmployment: values.startDateofEmployment,
      employmentZeroHourContract: Boolean(values.employmentZeroHourContract),
      employmentRefereeName: values.employmentRefereeName,
      employmentRefereePosition: values.employmentRefereePosition,
      employmentRefereeContactNumber: values.employmentRefereeContactNumber,
      employmentRefereeContactCountryID: parseEnumValue(values.employmentRefereeContactCountryID),
      employmentRefereeEmail: values.employmentRefereeEmail,
      countryOfEmployment: parseEnumValue(values.countryOfEmployment) || 2,
    };
  } else if (incomeTypeID === 2) {
    income.selfEmployed = {
      tradingTypeId: parseEnumValue(values.tradingType),
      startDateOfSelfEmployment: values.startDateOfSelfEmployment,
      selfEmployedGrossAnnualIncome: parseNumber(values.selfEmployedGrossAnnualIncome),
      netMonthlyIncome: parseNumber(values.selfEmployedNetMonthlyIncome),
      taxReturnSubmitted: Boolean(values.taxReturnSubmitted),
      doesApplicantUseAccountant: values.usesAccountant === 'Yes',
      accountantCompanyName: values.accountantCompanyName,
      accountantContactName: values.accountantContactName,
      accountantContactNoCountryId: parseEnumValue(values.accountantContactNoCountryId),
      accountantContactNumber: values.accountantContactNumber,
      accountantEmailAddress: values.accountantEmail,
      accountantContactPosition: values.accountantContactPosition,
      accountantAddress: buildUkAddress(values, 'accountant'),
      industryType: parseEnumValue(values.industryType),
    };
  } else if (incomeTypeID === 3) {
    income.pension = {
      pensionProvider: values.pensionProvider,
      netMonthlyIncome: parseNumber(values.pensionNetMonthlyIncome),
      grossAnnualIncome: parseNumber(values.pensionGrossAnnualIncome),
      startDateOfPension: values.startDateOfPension,
    };
  } else if (incomeTypeID === 5) {
    income.unemployed = {
      netMonthlyIncome: parseNumber(values.studentNetMonthlyIncome),
      grossAnnualIncome: parseNumber(values.studentGrossAnnualIncome),
      startDate: values.studentStartDate,
    };
  } else if (incomeTypeID === 6) {
    income.unemployed = {
      applicantBenefitsReceived: Boolean(values.benefitsReceived),
      grossAnnualIncome: parseNumber(values.unemployedGrossAnnualIncome),
      taxCredit: Boolean(values.taxCredit),
      carerAllowance: Boolean(values.carerAllowance),
      childBenefit: Boolean(values.childBenefit),
      disabilityBenefit: Boolean(values.disabilityBenefit),
      fosterAllowance: Boolean(values.fosterAllowance),
      guardianAllowance: Boolean(values.guardianAllowance),
      childMaintenance: Boolean(values.childMaintenance),
      housingBenefit: Boolean(values.housingBenefit),
      employmentSupport: Boolean(values.employmentSupport),
      universalCredit: Boolean(values.universalCredit),
      netMonthlyIncome: parseNumber(values.unemployedNetMonthlyIncome),
      startDate: values.unemployedStartDate,
    };
  } else if (incomeTypeID === 7) {
    income.saving = {
      savingsAmount: parseNumber(values.savingsAmount),
    };
  } else if (incomeTypeID === 8) {
    income.investment = {
      investmentSchemeOrCompany: values.investmentScheme,
      investmentAmount: parseNumber(values.investmentAmount),
    };
  }

  const addressHistory = Array.isArray(values.addressHistory) && values.addressHistory.length
    ? values.addressHistory
    : [{
      addressLine1: values.historyAddressLine1,
      town: values.historyTown,
      postcode: values.historyPostcode,
      country: values.historyCountry,
      isUkAddress: values.isUkAddress,
      addressType: values.addressType,
      monthMovedIn: values.monthMovedIn,
      yearMovedIn: values.yearMovedIn,
    }];
  const hasUkAddressInHistory = addressHistory.some((address) => address.country === '2');
  const ukCountryCode = parseEnumValue(values.ukCountryCode);
  const shouldNullTenancyTermMonths = parsedTenancyTermMonths > 36;
  const payload = {
    tenancyTermMonths: shouldNullTenancyTermMonths ? null : parsedTenancyTermMonths,
    tenancyType: ukCountryCode === 1 ? 1 : undefined,
    monthlyRental: parseNumber(values.monthlyRental) || 0,
    // Business decision: Letting Type is fixed and not user-editable in UI.
    lettingType: 2,
    estimatedMoveInDate: values.estimatedMoveInDate,
    propertyType: parseEnumValue(values.propertyType),
    propertyConstructionPeriod: parseNumber(values.propertyConstructionPeriod),
    numberOfBedrooms: parseNumber(values.numberOfBedrooms),
    noOfAdultOccupants: parseNumber(values.noOfAdultOccupants),
    noOfChildOccupants: parseNumber(values.noOfChildOccupants),
    isAdvanceRent: Boolean(values.isAdvanceRent),
    validateIncome: values.isAdvanceRent ? Boolean(values.validateIncome) : undefined,
    rentGuarantee: 4,
    propertyAddress: {
      houseNumber: values.houseNumber,
      houseName: values.houseName,
      addressLine1: values.addressLine1,
      addressLine2: values.addressLine2,
      town: values.town,
      postcode: values.postcode,
      ukCountryCode,
    },
    tenants: [
      {
        tenantTypeId: parseEnumValue(values.tenantTypeId),
        referenceType: 1,
        rightToRent: false,
        title: parseEnumValue(values.title),
        otherTitle: values?.title && String(values.title).startsWith('6') ? values.otherTitle : undefined,
        firstName: values.firstName,
        middleName: values.middleName,
        surname: values.surname,
        shareOfRent: parseNumber(values.shareOfRent),
        telephoneCountryID: parseEnumValue(values.telephoneCountryId),
        contactTelephoneNo: values.contactTelephoneNo,
        emailAddress: values.emailAddress,
        dateOfBirth: values.dateOfBirth,
        nationality: parseEnumValue(values.nationality),
        niNumber: normaliseNiNumber(values.niNumber),
        residentStatus: parseEnumValue(values.residentStatus),
        additionalInformation: values.additionalInformation,
        pet: Boolean(values.pet),
        petInfo: values.pet ? buildPetInfoSummary(values) : undefined,
        petData: values.pet ? {
          numberOfPets: 1,
          petDetail: [{
            petType: parseEnumValue(values.petType),
            petTypeDetail: values.petType === '3' ? values.petTypeDetail : undefined,
            petName: values.petName,
            petBreed: values.petBreed,
            petAge: parseNumber(values.petAge),
            petSex: parseEnumValue(values.petSex),
          }],
        } : undefined,
        otherNames: [{
          firstName: values.otherFirstName,
          middleName: values.otherMiddleName,
          lastName: values.otherLastName,
          nameChangeDate: values.nameChangeDate,
        }],
        addressOtherDetailsViewModel: [{
          addresses: addressHistory.map((address, index) => ({
            addressLine1: address.addressLine1,
            town: address.town,
            postcode: address.postcode,
            country: parseEnumValue(address.country),
            isUKAddress: Boolean(address.isUkAddress),
            addressTypeId: parseEnumValue(address.addressType || (index === 0 ? '1' : '2')),
            monthMovedIn: parseEnumValue(address.monthMovedIn),
            yearMovedIn: parseNumber(address.yearMovedIn),
          })),
          // HomeLet (confirmed directly with their team, 2026-07-31): if the applicant has a UK
          // address within their real address history, that's a normal Addresses[] entry above and
          // haveYouEverLivedInUk/ukAddress must be omitted entirely. Otherwise (address history is
          // fully overseas), haveYouEverLivedInUk must be sent explicitly (true/false), and whenever
          // it's true, ukAddress must be sent alongside it with move-in/move-out — it's never added
          // to Addresses[] itself.
          haveYouEverLivedInUk: hasUkAddressInHistory ? undefined : Boolean(values.haveYouEverLivedInUk),
          ukAddress: (!hasUkAddressInHistory && values.haveYouEverLivedInUk) ? {
            addressLine1: values.ukAddressLine1,
            town: values.ukTown,
            postcode: values.ukPostcode,
            monthMovedIn: parseEnumValue(values.ukMonthMovedIn),
            yearMovedIn: parseNumber(values.ukYearMovedIn),
            monthMovedOut: parseEnumValue(values.ukMonthMovedOut),
            yearMovedOut: parseNumber(values.ukYearMovedOut),
          } : undefined,
          residentialDetails: {
            currentResidentialStatus: parseEnumValue(values.currentResidentialStatus),
            currentRentAmount: parseNumber(values.currentRentAmount),
            landlordName: values.landlordName,
            landlordContactNumber: values.landlordContactNumber,
            landlordEmail: values.landlordEmail,
          },
          adverseCreditDetails: {
            submittedisAdverseCredit: Boolean(values.isAdverseCredit),
            submittedisCCJ: Boolean(values.ccj),
            submittedisBankruptcy: Boolean(values.bankruptcy),
            submittedisInsolvency: Boolean(values.insolvency),
            submittedisOtherAdverseCredit: Boolean(values.otherAdverseCreditDescription),
            submittedOtherAdverseCreditDesc: values.otherAdverseCreditDescription,
          },
        }],
        referencingTenantIncomeViewModel: [income],
        bankDetails: {
          bankSortCode: values.sortCode,
          bankAccountNumber: values.bankAccountNumber,
        },
      },
    ],
  };

  const cleanedPayload = cleanObject(payload) || {};

  // HomeLet requires Employed block when income type is Employed, even when
  // individual employed fields are left empty in UI.
  const primaryTenant = cleanedPayload?.tenants?.[0];
  const primaryIncomeList = primaryTenant?.referencingTenantIncomeViewModel;
  const primaryIncome = primaryIncomeList?.[0];
  if (primaryIncome && primaryIncome.incomeTypeID === 1 && !primaryIncome.employed) {
    primaryIncome.employed = {};
  }

  if (shouldNullTenancyTermMonths) {
    cleanedPayload.tenancyTermMonths = null;
  }

  return cleanedPayload;
};

const getRenewalQuotes = async (opportunityId) => {
  const { result = {} } = await API.get('ApiGW', `/salesforce/opportunity/${opportunityId}/quotes`);
  return mapRenewalQuotes(result.records);
};

const getQuotesWithUnits = async (opportunityId) => {
  const { result = [] } = await API.get('ApiGW', `/salesforce/opportunity/${opportunityId}/quotesWithUnits`);
  return mapQuotesByUnit(result.records);
};

const createContactAccount = async (accountInfo, opportunityId, role) => {
  const res = await API.post('ApiGW', '/salesforce/create-account', {
    body: {
      accountInfo,
      opportunityId,
      role: transformRoleToApi(role),
    },
  });
  return res;
};

const updatePhoneNumber = (contactId, phoneNumber) => {
  API.patch('ApiGW', `/salesforce/update-phone/${contactId}`, { body: { phoneNumber } });
};

const updateOpportunity = async (opportunityId, opportunityData) => {
  const { result = {} } = await API.patch('ApiGW', `/salesforce/opportunity/${opportunityId}`, { body: { opportunityData } });
  return result === '';
};

const updateOffer = async (offerId, offerData) => {
  const { result = {} } = await API.patch('ApiGW', `/salesforce/quote/${offerId}`, { body: { offerData } });
  return result;
};

const updateOpportunityContactRole = (opportunityContactRoleId, ocrData) => (
  API.patch('ApiGW', `/salesforce/opportunityContactRole/${opportunityContactRoleId}`, { body: { ocrData } })
);

const getHouseholdSummary = async (opportunityId) => {
  const { result: { records = [] } = {} } = await API.get('ApiGW', `/salesforce/household-summary/${opportunityId}`);
  return records;
};

const updateOpportunityGuarantorRequired = async (opportunityId, isGuarantorRequired) => {
  const { success = false } = await API.patch('ApiGW', `/salesforce/opportunity/${opportunityId}/guarantor-required`, {
    body: { isGuarantorRequired },
  });
  return success;
};

const updateOpportunityNoUkBankAccount = async (opportunityId, hasNoUkBankAccount) => {
  const { success = false } = await API.patch('ApiGW', `/salesforce/opportunity/${opportunityId}/no-uk-bank-account`, {
    body: { hasNoUkBankAccount },
  });
  return success;
};

const updateOpportunityExtendedTimeframeAgreed = async (opportunityId, extendedTimeframeAgreed) => {
  const { success = false } = await API.patch('ApiGW', `/salesforce/opportunity/${opportunityId}/extended-timeframe-agreed`, {
    body: { extendedTimeframeAgreed },
  });
  return success;
};

const getHouseholdReferences = async (opportunityId) => {
  const { result = {} } = await API.get('ApiGW', `/salesforce/opportunity/${opportunityId}/OCRReferences`);
  return transformHouseholdReferences(result);
};

const getTenantNames = async (opportunityId) => {
  const { result = {} } = await API.get('ApiGW', `/salesforce/household/${opportunityId}/names`);
  return (result?.records || []).map(mapNames);
};

const getPrimaryOffer = async (opportunityId) => {
  const { result = {} } = await API.get('ApiGW', `/salesforce/offer/${opportunityId}`);
  return result?.records?.[0] ? transformFromGetPrimaryOffer(result.records[0]) : undefined;
};

const getUnit = async (unitId) => {
  const { result = {} } = await API.get('ApiGW', `/salesforce/unit/${unitId}`);
  return transformUnitFromApi(result);
};

const unitPrimaryImageCache = new Map();

const getUnitPrimaryImage = async (unitId) => {
  if (unitPrimaryImageCache.has(unitId)) {
    return unitPrimaryImageCache.get(unitId);
  }
  const { result = {} } = await API.get('ApiGW', `/salesforce/unit/${unitId}/primaryImage`);
  const url = result.records && result.records[0] && result.records[0].Media_Link_URL__c;
  if (url) {
    unitPrimaryImageCache.set(unitId, url);
    return url;
  }
  return null;
};

const getContactActiveOpportunity = async (contactId) => {
  const { result = {} } = await API.get('ApiGW', `/salesforce/contact/${contactId}/activeOCR`);
  return result.done && selectPreferredActiveOcrRecord(result.records);
};

const getOpportunity = async (opportunityId) => {
  const { result = {} } = await API.get('ApiGW', `/salesforce/opportunity/${opportunityId}`);
  return transformFromGetOpportunity(result);
};

const updateContact = async (contactId, ocrId, accountInfo) => {
  const { result, success, error } = await API.post('ApiGW', `/salesforce/contact/${contactId}`, { body: { accountInfo, ocrId } });
  if (success) {
    return result;
  }
  throw new Error(error);
};

const raiseAdvancePayment = async (paymentPayload) => {
  const { result, success, error } = await API.post('ApiGW', '/salesforce/payment', { body: { paymentPayload } });
  if (success) {
    return result;
  }
  throw new Error(error);
};

const raiseAuthorisationRecord = async (contactId, opportunityId) => {
  const { result = {} } = await API.post('ApiGW', '/salesforce/authorisation', { body: { contactId, opportunityId } });
  return result;
};

const getHomeletValidationMessage = (details, fallback) => {
  const errors = details && details.payload && details.payload.errors;
  if (errors && typeof errors === 'object') {
    const messages = Object.values(errors)
      .reduce((acc, value) => acc.concat(Array.isArray(value) ? value : [value]), [])
      .filter(Boolean);

    if (messages.length) {
      return messages.join(' ');
    }
  }

  return (details && details.payload && details.payload.detail)
    || (details && details.payload && details.payload.title)
    || (details && details.message)
    || fallback;
};

const submitHomeletReference = async (opportunityId, contactId, homeletFormValues) => {
  const payload = mapHomeletFormToPayload(homeletFormValues);
  const {
    result = {},
    success = true,
    error,
    details,
  } = await API.post('ApiGW', `/salesforce/homelet/reference/${opportunityId}/${contactId}`, {
    body: { payload },
  });
  if (!success) {
    const detailedError = getHomeletValidationMessage(details, error);
    throw new Error(detailedError || 'HomeLet reference submission failed.');
  }
  return result;
};

const uploadHomeletTenancyDocument = async (opportunityId, contactId, documentPayload) => {
  const response = await API.post(
    'ApiGW',
    `/salesforce/homelet/tenancy-document/${opportunityId}/${contactId}`,
    { body: documentPayload },
  );
  const {
    result = {},
    success = true,
    error,
    details,
    homeletSuccess,
    homeletMessage,
  } = response || {};

  if (!success) {
    const detailedError = (details && details.message)
      || (details && details.payload && details.payload.detail)
      || (details && details.payload && details.payload.title)
      || error;
    throw new Error(detailedError || 'Document upload failed.');
  }

  return {
    result,
    homeletSuccess,
    homeletMessage,
  };
};

export {
  createContactAccount,
  updatePhoneNumber,
  getPrimaryOffer,
  getHouseholdSummary,
  updateOpportunityGuarantorRequired,
  updateOpportunityNoUkBankAccount,
  updateOpportunityExtendedTimeframeAgreed,
  getHouseholdReferences,
  getOpportunityStage,
  getRenewalQuotes,
  getUnitPrimaryImage,
  getQuotesWithUnits,
  getUnit,
  updateOpportunityContactRole,
  getContactActiveOpportunity,
  updateContact,
  getOcrReferencing,
  updateOpportunity,
  updateOffer,
  getOpportunity,
  raiseAdvancePayment,
  getOpportunityPayments,
  getPaymentRecord,
  raiseAuthorisationRecord,
  getAuthorisationRecord,
  getContactAuthorisations,
  getContactAuthorisationsList,
  getTenantNames,
  getOcrDdAmount,
  mapHomeletFormToPayload,
  submitHomeletReference,
  uploadHomeletTenancyDocument,
};
